import { useAtomValue } from 'jotai'
import React from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import { extensionsListAtom } from '../../atoms'
import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'
import { NodeSelect } from './NodeSelect'

export interface FaxSelectorProps {
  /** Current selected fax extension ID */
  value?: string
  /** Callback when fax extension selection changes */
  onChange: (faxExtensionId: string | undefined) => void
  /** Placeholder text for the select */
  placeholder?: string
  /** Label for the floating label */
  label?: string
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  // Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Reusable fax extension selector dropdown component.
 * Automatically filters to only show Virtual Fax Machine extensions (ExtensionVirtualFaxMachine type).
 *
 * Data Source: ExtensionVirtualFaxMachine types from extensionsListAtom (Jotai atom)
 * Fetched via: GET /app/v1/extensions/?pbx_id={id}&type=ExtensionVirtualFaxMachine
 * Documentation: libraries/switchboard-ui/NODE_DATA_POPULATION/fax-detect.md
 * Component: FaxDetectNode uses FaxSelector
 */
export const FaxSelector: React.FC<FaxSelectorProps> = ({
  value = '',
  onChange,
  placeholder: placeholderProp,
  label: labelProp,
  t,
}) => {
  const placeholder =
    placeholderProp ??
    text(t, 'switchboard.core.selectFax', 'Select fax extension...')
  const label =
    labelProp ?? text(t, 'switchboard.core.faxLabel', 'Fax Extension')
  const extensionsList = useAtomValue(extensionsListAtom)
  const availableFaxes = React.useMemo(() => {
    if (!extensionsList) return []
    return extensionsList.filter((ext) => {
      if (ext.type !== 'ExtensionVirtualFaxMachine') return false
      const extWithActive = ext as ExtensionInstance & { active?: boolean }
      if ('active' in extWithActive) {
        return extWithActive.active === true
      }
      return true
    })
  }, [extensionsList])

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e.target.value || undefined)
    },
    [onChange],
  )

  return (
    <NodeSelect label={label} value={value} onChange={handleChange}>
      <option value="">{placeholder}</option>
      {availableFaxes.map((fax) => (
        <option key={fax.id} value={String(fax.id)}>
          {fax.number} -{' '}
          {fax.name || text(t, 'switchboard.core.unnamed', 'Unnamed')}
        </option>
      ))}
    </NodeSelect>
  )
}
